import React from 'react';
import { Element } from 'react-scroll';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  PencilSimpleIcon,
  TrashIcon,
  CaretDownIcon,
  CaretUpIcon,
  CurrencyDollarIcon,
  DotsSixVerticalIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import {
  RebateRule,
  RebateRuleTier,
  RebateRuleProductGroup,
  RebateRuleDivision,
  DivisionSimpleDTO,
} from '@erp/shared-types';
import {
  formatPercentage,
  formatCurrency,
  isConditionalRule,
} from '@erp/shared-utils';
import { formatThresholdRange } from 'src/utils/rebateUtils';
import { capitalizeWords } from 'src/utils/strings';
import { CoreEnumOptionAssociation } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';
import { CoreEnumOptionDTO } from 'src/api/core/core-enums/useCoreEnums';
import { TagsDisplay } from 'src/components/rebates/TagsDisplay';
import { ManualAmount } from 'src/api/rebates/useManualAmounts';

interface RuleCardDisplayProps {
  rule: RebateRule;
  isDragging?: boolean;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onOpenManualAmountModal?: () => void;
  divisions: DivisionSimpleDTO[];
  agreementStatus: 'draft' | 'finalized';
  validationErrors: string[];
  areProductGroupsSame: (rule: RebateRule) => boolean;
  ManualAmountsSection: React.ComponentType<{ rule: RebateRule }>;
  // Tags data (bulk fetched from parent)
  tagsByRuleId: Map<string, CoreEnumOptionAssociation[]>;
  availableTags: CoreEnumOptionDTO[];
  tagsLoading: boolean;
  onRuleTagAdd: (ruleId: string, tagId: string) => void;
  onRuleTagRemove: (ruleId: string, tagId: string) => void;
  tagsUpdating: boolean;
  // Manual amounts data (bulk fetched from parent)
  manualAmountsByRuleId?: Map<string, ManualAmount[]>;
  // Total rebate amount for this rule
  rebateTotal?: number;
  // Tier status by type (estimates, predictions, reported)
  tierStatus?: { estimates?: number; predictions?: number; reported?: number };
  // Volume totals for this rule (gross and discounted)
  volume?: {
    qualifyingVolume: number;
    affectedVolume: number;
    discountedQualifyingVolume: number;
    discountedAffectedVolume: number;
    discountPercentage: number;
  };
  // Agreement dates for context (for custom rule duration display)
  agreementStartDate?: string;
  agreementEndDate?: string;
}

/**
 * Parse a date string as a local date to avoid timezone shifts.
 * Input format expected: "YYYY-MM-DD" or ISO string
 */
function parseLocalDate(dateStr: string): Date {
  // Handle ISO strings or date-only strings
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a date range as a compact string
 */
function formatDateRangeCompact(startDate: string, endDate: string): string {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const startStr = start.toLocaleDateString('en-US', formatOptions);
  const endStr = end.toLocaleDateString('en-US', formatOptions);
  return `${startStr} - ${endStr}`;
}

// Product Group Section component
const ProductGroupSection = ({
  title,
  productGroups,
  inclusionType,
  chipColor,
  showWhenEmpty = false,
}: {
  title: string;
  productGroups: RebateRuleProductGroup[];
  inclusionType: 'inclusive' | 'exclusive';
  chipColor: 'success' | 'warning' | 'info';
  showWhenEmpty?: boolean;
}) => {
  if (
    !showWhenEmpty &&
    productGroups.length === 0 &&
    inclusionType !== 'exclusive'
  ) {
    return null;
  }

  const isAllProducts =
    inclusionType === 'exclusive' && productGroups.length === 0;
  const displayTitle = isAllProducts
    ? `All Products ${title}`
    : `${title} Product Groups (${productGroups.length})`;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-sm font-semibold">{displayTitle}</h4>
        {!isAllProducts && (
          <Badge
            variant="outline"
            className={
              inclusionType === 'inclusive'
                ? 'border-green-200 text-green-700'
                : 'border-yellow-200 text-yellow-700'
            }
          >
            {inclusionType === 'inclusive' ? 'Inclusive' : 'Exclusive'}
          </Badge>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {isAllProducts ? (
          <Badge variant="outline" className="border-gray-200 text-gray-700">
            All Products
          </Badge>
        ) : (
          [...productGroups]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((group: RebateRuleProductGroup) => (
              <Badge
                key={group.id}
                variant="outline"
                className={
                  chipColor === 'success'
                    ? 'border-green-200 text-green-700'
                    : chipColor === 'warning'
                      ? 'border-yellow-200 text-yellow-700'
                      : 'border-gray-200 text-gray-700'
                }
              >
                {group.name}
              </Badge>
            ))
        )}
      </div>
    </div>
  );
};

export const RuleCardDisplay = React.memo<RuleCardDisplayProps>(
  ({
    rule,
    isDragging,
    isExpanded,
    onToggleExpansion,
    onEdit,
    onDelete,
    onOpenManualAmountModal,
    divisions,
    agreementStatus,
    validationErrors,
    areProductGroupsSame,
    ManualAmountsSection,
    tagsByRuleId,
    availableTags,
    tagsLoading,
    onRuleTagAdd,
    onRuleTagRemove,
    tagsUpdating,
    // Manual amounts data (bulk fetched from parent)
    manualAmountsByRuleId,
    // Total rebate amount for this rule
    rebateTotal,
    // Tier status by type (estimates, predictions)
    tierStatus,
    // Volume totals for this rule
    volume,
    // Agreement dates for context
    agreementStartDate,
    agreementEndDate,
  }) => {
    const tierCount = rule.tiers?.length || 0;
    const divisionCount = rule.divisions?.length || 0;

    // Compute conditional status using shared utility
    const isConditional = isConditionalRule({
      thresholdType: rule.thresholdType,
      isManual: rule.isManual,
      tiers:
        rule.tiers?.map((tier) => ({
          thresholdValue: tier.thresholdValue,
        })) || [],
    });

    // Validation errors are now passed as props from parent
    const hasErrors = validationErrors.length > 0;

    // Use bulk-fetched tags from parent container
    const currentTags = tagsByRuleId.get(rule.id) || [];
    const currentTagIds = new Set(currentTags.map((t) => t.coreEnumOptionId));

    // Tag handlers from container
    const handleAddTag = (tagId: string) => {
      onRuleTagAdd(rule.id, tagId);
    };

    const handleRemoveTag = (tagId: string) => {
      onRuleTagRemove(rule.id, tagId);
    };

    // Get manual amounts count from bulk-fetched data
    const manualAmounts = manualAmountsByRuleId?.get(rule.id) || [];
    const manualAmountsCount = manualAmounts.length;

    // For manual rules, calculate total from manual amounts (not from forecast breakdowns)
    const effectiveRebateTotal = rule.isManual
      ? manualAmounts.reduce(
          (sum, amount) => sum + parseFloat(amount.amount),
          0,
        )
      : rebateTotal;

    const discountPercentage = rule.discountPercentage
      ? parseFloat(rule.discountPercentage)
      : 0;

    return (
      <Element name={`rule-${rule.id}`}>
        <Card
          className={`border overflow-hidden transition-shadow duration-200 hover:shadow-md ${
            isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''
          } ${hasErrors ? 'border-red-300 bg-red-50/30' : ''}`}
        >
          {/* Rule Header - Always Visible */}
          <div
            className="flex justify-between items-center p-6 cursor-pointer"
            onClick={onToggleExpansion}
          >
            <div className="flex items-center gap-4">
              {/* Drag Handle */}
              <div className="flex-shrink-0 text-muted-foreground hover:text-foreground">
                <DotsSixVerticalIcon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-medium">{rule.name}</h3>
                  {validationErrors.length > 0 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="destructive"
                            className="bg-red-100 text-red-800 hover:bg-red-200"
                          >
                            <WarningCircleIcon className="h-3 w-3 mr-1" />
                            {validationErrors.length} issue
                            {validationErrors.length > 1 ? 's' : ''}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            {validationErrors.map((error, idx) => (
                              <p key={idx}>• {error}</p>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                {rule.notes && (
                  <p className="text-sm text-muted-foreground mb-2 max-w-2xl">
                    {rule.notes}
                  </p>
                )}
                <div className="flex gap-2 items-center flex-wrap">
                  {rule.isManual ? (
                    <>
                      <Badge
                        variant="outline"
                        className="border-yellow-200 text-yellow-700"
                      >
                        Manual
                      </Badge>
                      {manualAmountsCount > 0 && (
                        <Badge
                          variant="outline"
                          className="border-blue-200 text-blue-700"
                        >
                          {manualAmountsCount} manual rebate
                          {manualAmountsCount !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <>
                      {rule.thresholdType && (
                        <Badge
                          variant="outline"
                          className={
                            rule.thresholdType === 'volume'
                              ? 'border-blue-200 text-blue-700'
                              : rule.thresholdType === 'growth'
                                ? 'border-green-200 text-green-700'
                                : 'border-gray-200 text-gray-700'
                          }
                        >
                          {capitalizeWords(rule.thresholdType)}
                        </Badge>
                      )}
                      {!rule.isManual && (
                        <Badge
                          variant="outline"
                          className={
                            isConditional
                              ? 'border-purple-200 text-purple-700'
                              : 'border-gray-200 text-gray-700'
                          }
                        >
                          {isConditional ? 'Conditional' : 'Non-Conditional'}
                        </Badge>
                      )}
                      {rule.thresholdType === 'growth' &&
                        rule.growthBaseline && (
                          <Badge
                            variant="outline"
                            className="border-green-200 text-green-700"
                          >
                            Baseline:{' '}
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                              notation: 'compact',
                            }).format(parseFloat(rule.growthBaseline))}
                          </Badge>
                        )}
                      {rule.isRetroactiveToDollarOne && (
                        <Badge
                          variant="outline"
                          className="border-green-200 text-green-700"
                        >
                          Retroactive
                        </Badge>
                      )}
                    </>
                  )}
                  {discountPercentage > 0 && (
                    <Badge
                      variant="outline"
                      className="border-yellow-200 text-yellow-700"
                    >
                      Discount: {discountPercentage.toFixed(1)}%
                    </Badge>
                  )}
                  {/* Custom Duration Badge - show if rule has custom dates */}
                  {(rule.startDate || rule.endDate) && (
                    <Badge
                      variant="outline"
                      className="border-blue-200 text-blue-700"
                    >
                      {formatDateRangeCompact(
                        rule.startDate ?? agreementStartDate ?? '',
                        rule.endDate ?? agreementEndDate ?? '',
                      )}
                    </Badge>
                  )}
                  {!rule.isManual && (
                    <span className="text-sm text-muted-foreground ml-2">
                      {tierCount} tier{tierCount !== 1 ? 's' : ''} •{' '}
                      {divisionCount} division
                      {divisionCount !== 1 ? 's' : ''}
                    </span>
                  )}

                  {/* Tags Section - Always show when not loading and tags are available */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <TagsDisplay
                      currentTags={currentTags}
                      availableTags={availableTags}
                      currentTagIds={currentTagIds}
                      onAddTag={handleAddTag}
                      onRemoveTag={handleRemoveTag}
                      isUpdating={tagsUpdating}
                      isLoading={tagsLoading}
                      layout="compact"
                    />
                  </div>
                </div>

                {/* Volume & Rebate Metrics Table - Always visible for non-manual rules */}
                {!rule.isManual &&
                  volume !== undefined &&
                  effectiveRebateTotal !== undefined && (
                    <div className="mt-3 bg-black-50 border-2 border-black-200 rounded-lg p-3 w-fit">
                      {(() => {
                        // Check if qualifying and affected volumes are the same (accounting for floating point precision)
                        const EPSILON = 0.01;
                        const areVolumesSame =
                          Math.abs(
                            volume.qualifyingVolume - volume.affectedVolume,
                          ) < EPSILON &&
                          (volume.discountPercentage === 0 ||
                            Math.abs(
                              volume.discountedQualifyingVolume -
                                volume.discountedAffectedVolume,
                            ) < EPSILON);

                        return (
                          <Table className="w-auto">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="h-8 text-xs font-semibold"></TableHead>
                                {areVolumesSame ? (
                                  <TableHead className="h-8 text-xs font-semibold text-right">
                                    Qualifying & Affected
                                  </TableHead>
                                ) : (
                                  <>
                                    <TableHead className="h-8 text-xs font-semibold text-right">
                                      Qualifying
                                    </TableHead>
                                    <TableHead className="h-8 text-xs font-semibold text-right">
                                      Affected
                                    </TableHead>
                                  </>
                                )}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell className="py-1 text-xs font-medium">
                                  Volume (Gross)
                                </TableCell>
                                {areVolumesSame ? (
                                  <TableCell className="py-1 text-xs text-right">
                                    {formatCurrency(volume.qualifyingVolume)}
                                  </TableCell>
                                ) : (
                                  <>
                                    <TableCell className="py-1 text-xs text-right">
                                      {formatCurrency(volume.qualifyingVolume)}
                                    </TableCell>
                                    <TableCell className="py-1 text-xs text-right">
                                      {formatCurrency(volume.affectedVolume)}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                              {/* Show Volume after Discount row when there's a discount */}
                              {volume.discountPercentage > 0 && (
                                <TableRow>
                                  <TableCell className="py-1 text-xs font-medium">
                                    Volume (Discount{' '}
                                    {volume.discountPercentage.toFixed(1)}%)
                                  </TableCell>
                                  {areVolumesSame ? (
                                    <TableCell className="py-1 text-xs text-right text-yellow-700">
                                      {formatCurrency(
                                        volume.discountedQualifyingVolume,
                                      )}
                                    </TableCell>
                                  ) : (
                                    <>
                                      <TableCell className="py-1 text-xs text-right text-yellow-700">
                                        {formatCurrency(
                                          volume.discountedQualifyingVolume,
                                        )}
                                      </TableCell>
                                      <TableCell className="py-1 text-xs text-right text-yellow-700">
                                        {formatCurrency(
                                          volume.discountedAffectedVolume,
                                        )}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                              )}
                              <TableRow>
                                <TableCell className="py-1 text-xs font-medium">
                                  Rebate %
                                </TableCell>
                                {areVolumesSame ? (
                                  <TableCell className="py-1 text-xs text-right">
                                    {/* Use discounted volume for rebate % calculation (matches tier calculation) */}
                                    {(volume.discountPercentage > 0
                                      ? volume.discountedQualifyingVolume
                                      : volume.qualifyingVolume) > 0
                                      ? formatPercentage(
                                          Math.round(
                                            (effectiveRebateTotal /
                                              (volume.discountPercentage > 0
                                                ? volume.discountedQualifyingVolume
                                                : volume.qualifyingVolume)) *
                                              10000,
                                          ) / 100,
                                        )
                                      : '-'}
                                  </TableCell>
                                ) : (
                                  <>
                                    <TableCell className="py-1 text-xs text-right">
                                      {/* Use discounted volume for rebate % calculation (matches tier calculation) */}
                                      {(volume.discountPercentage > 0
                                        ? volume.discountedQualifyingVolume
                                        : volume.qualifyingVolume) > 0
                                        ? formatPercentage(
                                            Math.round(
                                              (effectiveRebateTotal /
                                                (volume.discountPercentage > 0
                                                  ? volume.discountedQualifyingVolume
                                                  : volume.qualifyingVolume)) *
                                                10000,
                                            ) / 100,
                                          )
                                        : '-'}
                                    </TableCell>
                                    <TableCell className="py-1 text-xs text-right">
                                      {/* Use discounted volume for rebate % calculation (matches tier calculation) */}
                                      {(volume.discountPercentage > 0
                                        ? volume.discountedAffectedVolume
                                        : volume.affectedVolume) > 0
                                        ? formatPercentage(
                                            Math.round(
                                              (effectiveRebateTotal /
                                                (volume.discountPercentage > 0
                                                  ? volume.discountedAffectedVolume
                                                  : volume.affectedVolume)) *
                                                10000,
                                            ) / 100,
                                          )
                                        : '-'}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  colSpan={areVolumesSame ? 2 : 3}
                                  className="py-1 text-xs font-semibold"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Total Rebate</span>
                                    <span
                                      className={
                                        effectiveRebateTotal >= 0
                                          ? 'text-green-700'
                                          : 'text-red-700'
                                      }
                                    >
                                      {formatCurrency(effectiveRebateTotal)}
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        );
                      })()}
                    </div>
                  )}

                {/* Manual rule rebate total */}
                {rule.isManual && effectiveRebateTotal !== undefined && (
                  <div className="mt-3 flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={
                        effectiveRebateTotal >= 0
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-red-50 text-red-700 border-red-200'
                      }
                    >
                      Total Rebate: {formatCurrency(effectiveRebateTotal)}
                    </Badge>
                    {onOpenManualAmountModal && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenManualAmountModal();
                                }}
                                className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                              >
                                <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                                <span className="text-xs">Add Amount</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Add Manual Amount</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpansion();
                }}
                className="h-8 w-8 p-0"
              >
                {isExpanded ? (
                  <CaretUpIcon className="h-4 w-4" />
                ) : (
                  <CaretDownIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Collapsible Content */}
          {isExpanded && (
            <CardContent className="pt-0 px-6 pb-6">
              <div>
                {/* Growth Baseline Info for Growth-based Rules */}
                {!rule.isManual && rule.thresholdType === 'growth' && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold mb-2">
                      Growth Configuration
                    </h4>
                    <div className="flex gap-4 items-center flex-wrap">
                      <Badge
                        variant="outline"
                        className={
                          rule.growthBaseline
                            ? 'border-blue-200 text-blue-700'
                            : 'border-red-200 text-red-700'
                        }
                      >
                        Baseline:{' '}
                        {rule.growthBaseline
                          ? new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(parseFloat(rule.growthBaseline))
                          : 'Not set'}
                      </Badge>
                      <p className="text-sm text-muted-foreground">
                        Tier thresholds represent percentage of baseline (e.g.,
                        105% = 5% growth, 90% = 10% shrinkage)
                      </p>
                    </div>
                  </div>
                )}

                {/* Product Groups */}
                {!rule.isManual && (
                  <div className="mb-6">
                    {areProductGroupsSame(rule) ? (
                      <ProductGroupSection
                        title="Qualifying & Affected"
                        productGroups={rule.qualifyingProductGroups}
                        inclusionType={
                          (rule.qualifyingInclusionType as
                            | 'inclusive'
                            | 'exclusive') || 'inclusive'
                        }
                        chipColor="info"
                        showWhenEmpty={true}
                      />
                    ) : (
                      <>
                        <ProductGroupSection
                          title="Qualifying"
                          productGroups={rule.qualifyingProductGroups}
                          inclusionType={
                            (rule.qualifyingInclusionType as
                              | 'inclusive'
                              | 'exclusive') || 'inclusive'
                          }
                          chipColor="info"
                          showWhenEmpty={true}
                        />
                        <ProductGroupSection
                          title="Affected"
                          productGroups={rule.affectedProductGroups}
                          inclusionType={
                            (rule.affectedInclusionType as
                              | 'inclusive'
                              | 'exclusive') || 'inclusive'
                          }
                          chipColor="info"
                          showWhenEmpty={false}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Divisions */}
                {rule.divisions && rule.divisions.length > 0 && (
                  <div className="mb-6">
                    {divisions.length > 0 &&
                    rule.divisions.length === divisions.length ? (
                      <>
                        <h4 className="text-sm font-semibold mb-2">
                          All Divisions
                        </h4>
                        <Badge
                          variant="outline"
                          className="border-gray-200 text-gray-700"
                        >
                          All Divisions
                        </Badge>
                      </>
                    ) : (
                      <>
                        <h4 className="text-sm font-semibold mb-2">
                          Applicable Divisions ({rule.divisions.length})
                        </h4>
                        <div className="flex gap-2 flex-wrap">
                          {[...rule.divisions]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((division: RebateRuleDivision) => (
                              <Badge
                                key={division.id}
                                variant="outline"
                                className="border-gray-200 text-gray-700"
                              >
                                {division.name}
                              </Badge>
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Tiers or Manual Amounts */}
                <div>
                  {rule.isManual ? (
                    <ManualAmountsSection rule={rule} />
                  ) : (
                    <>
                      <h4 className="text-sm font-semibold mb-4">
                        Rebate Tiers ({rule.tiers?.length || 0})
                      </h4>
                      {rule.tiers && rule.tiers.length > 0 ? (
                        <div className="border rounded-md">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="font-semibold">
                                  Tier
                                </TableHead>
                                <TableHead className="font-semibold">
                                  {rule.thresholdType === 'growth'
                                    ? '% of Baseline / Dollar Threshold'
                                    : 'Threshold Range'}
                                </TableHead>
                                <TableHead className="font-semibold">
                                  Rebate %
                                </TableHead>
                                <TableHead className="font-semibold">
                                  Status
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rule.tiers
                                .sort(
                                  (a: RebateRuleTier, b: RebateRuleTier) =>
                                    a.tierOrder - b.tierOrder,
                                )
                                .map((tier: RebateRuleTier) => {
                                  const isEstimateTier =
                                    tierStatus?.estimates === tier.tierOrder;
                                  const isPredictionTier =
                                    tierStatus?.predictions === tier.tierOrder;
                                  const isReportedTier =
                                    tierStatus?.reported === tier.tierOrder;
                                  const hasAnyBadge =
                                    isEstimateTier ||
                                    isPredictionTier ||
                                    isReportedTier;
                                  return (
                                    <TableRow
                                      key={tier.id}
                                      className={
                                        !hasAnyBadge ? 'opacity-50' : ''
                                      }
                                    >
                                      <TableCell>
                                        <Badge
                                          variant="outline"
                                          className="border-gray-200 text-gray-700"
                                        >
                                          {tier.tierOrder}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <span className="text-sm font-medium">
                                          {formatThresholdRange(
                                            rule.tiers,
                                            tier.tierOrder,
                                            rule.thresholdType,
                                            rule.growthBaseline ?? undefined,
                                          )}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <span className="text-sm font-medium text-gray-900">
                                          {formatPercentage(
                                            parseFloat(tier.rebateValue),
                                          )}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex gap-1 flex-wrap">
                                          {isEstimateTier && (
                                            <Badge
                                              variant="outline"
                                              className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                                            >
                                              Est
                                            </Badge>
                                          )}
                                          {isPredictionTier && (
                                            <Badge
                                              variant="outline"
                                              className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
                                            >
                                              Proj
                                            </Badge>
                                          )}
                                          {isReportedTier && (
                                            <Badge
                                              variant="outline"
                                              className="bg-gray-50 text-gray-700 border-gray-200 text-xs"
                                            >
                                              Reported
                                            </Badge>
                                          )}
                                          {!hasAnyBadge && (
                                            <span className="text-xs text-muted-foreground">
                                              -
                                            </span>
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-muted-foreground">
                            No tiers defined for this rule
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </Element>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if rule, isDragging, or isExpanded changed
    return (
      prevProps.rule.id === nextProps.rule.id &&
      JSON.stringify(prevProps.rule) === JSON.stringify(nextProps.rule) &&
      prevProps.isDragging === nextProps.isDragging &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.tagsLoading === nextProps.tagsLoading &&
      prevProps.availableTags === nextProps.availableTags &&
      prevProps.tagsByRuleId === nextProps.tagsByRuleId &&
      prevProps.tagsUpdating === nextProps.tagsUpdating &&
      prevProps.manualAmountsByRuleId === nextProps.manualAmountsByRuleId &&
      prevProps.rebateTotal === nextProps.rebateTotal &&
      JSON.stringify(prevProps.tierStatus) ===
        JSON.stringify(nextProps.tierStatus) &&
      JSON.stringify(prevProps.validationErrors) ===
        JSON.stringify(nextProps.validationErrors) &&
      JSON.stringify(prevProps.volume) === JSON.stringify(nextProps.volume)
    );
  },
);

RuleCardDisplay.displayName = 'RuleCardDisplay';
